import { useEffect, useRef, useState } from 'react';
import { qcErrorMessage } from '../../../services/qcApiError';
import axios from 'axios';
import { qualityCheckServices } from '../../../services/qualityCheck';
import { QcCategoriesState } from './qcCategories';

/**
 * SKIN-262 — fetch QC feedback categories once per eCRF image field name.
 *
 * Several shots on a lesion share a field; a per-image fetch would multiply requests
 * by ~5. The cache key is the field name, matching what the API is keyed on.
 */
export function useQcCategoriesCache(
    uuidInvestigation: string | undefined,
    fieldNames: string[],
): Record<string, QcCategoriesState> {
    const [cache, setCache] = useState<Record<string, QcCategoriesState>>({});
    const inFlight = useRef<Set<string>>(new Set());
    const uniqueKey = Array.from(new Set(fieldNames.filter(Boolean))).sort().join('|');

    useEffect(() => {
        if (!uuidInvestigation || !uniqueKey) return;

        const names = uniqueKey.split('|');
        const sources = names.map((name) => {
            if (cache[name]?.status === 'ready' || inFlight.current.has(name)) return null;
            inFlight.current.add(name);
            setCache((prev) => ({ ...prev, [name]: { status: 'loading' } }));
            const source = axios.CancelToken.source();
            qualityCheckServices.getQcFeedbackCategories(uuidInvestigation, name, source.token)
                .then((sections) => {
                    setCache((prev) => ({ ...prev, [name]: { status: 'ready', sections } }));
                })
                .catch((err) => {
                    if (axios.isCancel(err)) return;
                    setCache((prev) => ({
                        ...prev,
                        [name]: {
                            status: 'failed',
                            error: qcErrorMessage(err, 'Failed to load categories'),
                        },
                    }));
                })
                .finally(() => {
                    inFlight.current.delete(name);
                });
            return source;
        });

        return () => {
            for (const source of sources) source?.cancel('superseded');
        };
        // Intentionally keyed on uniqueKey + investigation, not `cache`, so a ready
        // entry does not re-trigger the effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuidInvestigation, uniqueKey]);

    return cache;
}
